<script setup lang="ts" generic="T">
import type { CommandPaletteGroup, CommandPaletteItem, PopoverProps } from '@nuxt/ui';
import type { RouteLocationRaw } from 'vue-router';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { BreadcrumbSwitcherButton, destructiveItem, destructiveItemLabel, destructiveItemLeadingIcon } from '#layers/ui/public';

interface ItemProps<T> {
	getId: (item: T) => string;
	getTitle: (item: T) => string;
	getIcon: (item: T) => string | undefined;
	getRoute: (item: T) => RouteLocationRaw;
}

interface Labels {
	create: string;
	delete: string;
	edit: string;
	group: string;
}

interface Tooltips {
	create: string;
	delete: string;
	edit: string;
}

interface SpaceOrPageSelectorProps<T> {
	items: T[];
	currentItem?: T;
	currentTitle?: string;
	groupsBefore?: CommandPaletteGroup<CommandPaletteItem>[];
	canCreate: boolean;
	canEdit: boolean;
	itemProps: ItemProps<T>;
	labels: Labels;
	tooltips: Tooltips;
	placeholder?: string;
	content?: PopoverProps['content'];
	/**
	 * Render the item group and its create/edit/delete actions. Set to `false` to leave only
	 * `groupsBefore`, for callers whose underlying concept is not exposed at all.
	 */
	showItems?: boolean;
}

interface SpaceOrPageSelectorEmits {
	edit: [];
	create: [];
	delete: [];
}

type CommandPaletteItemSelectArgs = Parameters<NonNullable<CommandPaletteItem['onSelect']>>;

const {
	showItems = true,
	groupsBefore: groupsBeforeProp,
	labels,
	items,
	itemProps,
	currentItem,
	canEdit,
	tooltips,
	canCreate,
	currentTitle: currentTitleProp,
} = defineProps<SpaceOrPageSelectorProps<T>>();
const emit = defineEmits<SpaceOrPageSelectorEmits>();

const popoverOpen = defineModel<boolean>('popoverOpen', { default: false });

const appConfig = useAppConfig();
const { t } = useI18n();

const groupsBefore = computed(() =>
	groupsBeforeProp?.map((group) => ({
		...group,
		items: group.items?.map((item) => ({
			...item,
			onSelect(...args: CommandPaletteItemSelectArgs) {
				item.onSelect?.(...args);
				popoverOpen.value = false;
			},
		})),
	})) ?? [],
);

const itemGroup = computed(() => ({
	id: 'items',
	label: labels.group,
	items: items.map((item) => ({
		label: itemProps.getTitle(item),
		icon: itemProps.getIcon(item),
		active: currentItem ? itemProps.getId(currentItem) === itemProps.getId(item) : false,
		to: itemProps.getRoute(item),
		onSelect() {
			popoverOpen.value = false;
		},
	})),
}));

const actionGroups = computed(() => [
	{
		id: 'actions',
		ignoreFilter: true,
		items: [
			{
				label: labels.edit,
				icon: 'lucide:pencil',
				disabled: !canEdit,
				tooltip: !canEdit ? tooltips.edit : undefined,
				onSelect() {
					if (!canEdit) return;
					popoverOpen.value = false;
					emit('edit');
				},
			},
			{
				label: labels.create,
				icon: 'lucide:plus',
				disabled: !canCreate,
				tooltip: !canCreate ? tooltips.create : undefined,
				onSelect() {
					if (!canCreate) return;
					popoverOpen.value = false;
					emit('create');
				},
			},
		],
	},
	{
		ignoreFilter: true,
		id: 'more-actions',
		items: [{
			icon: appConfig.ui.icons.trash,
			type: 'delete',
			ui: {
				item: destructiveItem,
				itemLabelBase: destructiveItemLabel,
				itemLeadingIcon: destructiveItemLeadingIcon,
			},
			label: labels.delete,
			disabled: !canEdit,
			tooltip: !canEdit ? tooltips.delete : undefined,
			onSelect() {
				if (!canEdit) return;
				popoverOpen.value = false;
				emit('delete');
			},
		}],
	},
] as CommandPaletteGroup<CommandPaletteItem>[]);

const commandGroups = computed(() => [
	...groupsBefore.value,
	...(showItems ? [itemGroup.value, ...actionGroups.value] : []),
] as CommandPaletteGroup<CommandPaletteItem>[]);

const selectedCommand = computed(() =>
	commandGroups.value.flatMap((group) => group.items ?? []).find((item) => item.active),
);

const currentTitle = computed(() =>
	currentTitleProp ?? selectedCommand.value?.label ?? getCurrentItemTitle(),
);

const totalItems = computed(() =>
	commandGroups.value.reduce((count, group) => count + (group.items?.length ?? 0), 0),
);

function getCurrentItemTitle() {
	return currentItem ? itemProps.getTitle(currentItem) : '';
}
</script>

<template>
	<span class="inline-flex min-w-0 items-center gap-0.5">
		<DCommandPopover
			v-model:open="popoverOpen"
			:model-value="selectedCommand"
			:placeholder="placeholder || t('common.search')"
			:content
			:popover="{ ui: { content: 'min-w-55 max-w-80' } }"
			:groups="commandGroups"
			:fuse="{ resultLimit: totalItems }"
		>
			<slot :open="popoverOpen" :current-title>
				<BreadcrumbSwitcherButton :label="currentTitle" :pressed="popoverOpen" />
			</slot>

			<template #item="{ item }">
				<DTooltip
					:disabled="!item.disabled"
					:text="item.tooltip"
					:content="{
						side: 'right',
						sideOffset: -2,
					}"
				>
					<div class="flex w-full min-w-0 items-center gap-2">
						<DIconOrImage
							v-if="item.icon"
							:icon="item.icon"
							class="size-4 flex-none"
							:class="item.ui?.itemLeadingIcon ?? 'text-toned group-data-disabled:text-disabled'"
						/>
						<span class="truncate" :class="item.ui?.itemLabelBase as any">
							{{ item.label }}
						</span>
						<DIcon
							v-if="item.active"
							name="lucide:check"
							class="text-normal ms-auto size-4 flex-none"
						/>
					</div>
				</DTooltip>
			</template>
		</DCommandPopover>
	</span>
</template>
